import React, { useState } from 'react';
import Calendar from 'react-calendar';
import { useTranslation } from 'react-i18next';
import Spring from '@/components/Spring';
import LegendItem from '@/ui/LegendItem';
import styles from './styles.module.scss';
import 'react-calendar/dist/Calendar.css';

interface Match {
  id: number;
  date: string;
  homeTeam: string;
  awayTeam: string;
  tournament: { id: number; name: string; color: string };
  hasPrediction: boolean;
}

interface GamesCalendarProps {
  matches?: Match[];
  onDateSelect?: (date: Date, matches: Match[]) => void;
  onMatchClick?: (matchId: number) => void;
}

// Mock data for demo
const mockMatches: Match[] = [
  {
    id: 1,
    date: new Date().toISOString(),
    homeTeam: 'Real Madrid',
    awayTeam: 'Barcelona',
    tournament: { id: 1, name: 'La Liga', color: 'purple' },
    hasPrediction: true
  },
  {
    id: 2,
    date: new Date(Date.now() + 86400000 * 2).toISOString(),
    homeTeam: 'Man United',
    awayTeam: 'Liverpool',
    tournament: { id: 2, name: 'Premier League', color: 'green' },
    hasPrediction: false
  },
  {
    id: 3,
    date: new Date(Date.now() + 86400000 * 5).toISOString(),
    homeTeam: 'Bayern',
    awayTeam: 'Dortmund',
    tournament: { id: 3, name: 'Bundesliga', color: 'highlight' },
    hasPrediction: false
  }
];

const GamesCalendar: React.FC<GamesCalendarProps> = ({
  matches = mockMatches,
  onDateSelect,
  onMatchClick
}) => {
  const { t } = useTranslation('tournament');
  const { t: tCommon } = useTranslation('common');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);

  const getMatchesForDate = (date: Date) => {
    return matches.filter((match) => {
      const matchDate = new Date(match.date);
      return matchDate.toDateString() === date.toDateString();
    });
  };

  const getTournamentColors = () => {
    const colors = new Map<string, string>();
    matches.forEach((match) => {
      if (!colors.has(match.tournament.name)) {
        colors.set(match.tournament.name, match.tournament.color);
      }
    });
    return Array.from(colors.entries()).map(([name, color]) => ({ name, color }));
  };

  const handleDateClick = (value: Date) => {
    const dateMatches = getMatchesForDate(value);
    if (dateMatches.length > 0) {
      setSelectedDate(value);
      setPopupOpen(true);
      onDateSelect?.(value, dateMatches);
    }
  };

  const tileContent = ({ date }: { date: Date }) => {
    const dateMatches = getMatchesForDate(date);
    if (dateMatches.length > 0) {
      const color = dateMatches[0].tournament.color;
      return (
        <span className={styles.overlay}>
          <span
            className={styles.overlayBar}
            style={{ backgroundColor: `var(--${color})` }}
          />
        </span>
      );
    }
    return null;
  };

  const tileClassName = ({ date }: { date: Date }) => {
    const dateMatches = getMatchesForDate(date);
    return dateMatches.length > 0 ? styles.hasMatch : '';
  };

  const selectedMatches = selectedDate ? getMatchesForDate(selectedDate) : [];
  const legendItems = getTournamentColors();

  return (
    <Spring className="card">
      <div className={styles.main}>
        <h3 className={styles.title}>{t('gamesCalendar')}</h3>
        <div className={styles.calendarWrapper}>
          <Calendar
            locale="en-US"
            tileContent={tileContent}
            tileClassName={tileClassName}
            onClickDay={handleDateClick}
            prev2Label={null}
            next2Label={null}
            formatShortWeekday={(locale, date) =>
              date.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2)
            }
          />
        </div>
      </div>

      <div className={styles.legend}>
        {legendItems.map((item) => (
          <LegendItem key={item.name} text={item.name} color={item.color} />
        ))}
      </div>

      {popupOpen && selectedMatches.length > 0 && (
        <div className={styles.backdrop} onClick={() => setPopupOpen(false)}>
          <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.closeBtn}
              onClick={() => setPopupOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
            <div className={styles.matchesList}>
              {selectedMatches.map((match) => (
                <div
                  key={match.id}
                  className={styles.matchItem}
                  onClick={() => onMatchClick?.(match.id)}
                >
                  <span
                    className={styles.matchColor}
                    style={{ backgroundColor: `var(--${match.tournament.color})` }}
                  />
                  <div className={styles.matchInfo}>
                    <span className={styles.matchTeams}>
                      {match.homeTeam} {tCommon('vs').toLowerCase()} {match.awayTeam}
                    </span>
                    <span className={styles.matchTime}>
                      {new Date(match.date).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  {match.hasPrediction && (
                    <span className={styles.predicted}>✓</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Spring>
  );
};

export default GamesCalendar;
